import {
    cropImageToBlob,
    loadImageElement,
    normaliseRotation,
    rotatedSource,
} from '../../image/crop/cropImageEngine';

/**
 * Edits every image tool can offer on the files a user has picked.
 *
 * These produce a NEW `File` and the tool swaps it into its own list, so the
 * rest of the pipeline — compress, convert, resize, watermark, PDF — needs to
 * know nothing about cropping or rotation: it only ever sees files.
 */

const ENCODERS = {
    'image/png': { mime: 'image/png', ext: 'png', quality: null },
    'image/webp': { mime: 'image/webp', ext: 'webp', quality: 0.94 },
    'image/jpeg': { mime: 'image/jpeg', ext: 'jpg', quality: 0.94 },
};

/** Keep the original format where the canvas can write it; JPEG otherwise. */
function encoderFor(file) {
    const byType = ENCODERS[file.type];
    if (byType) return byType;
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (ext === 'png') return ENCODERS['image/png'];
    if (ext === 'webp') return ENCODERS['image/webp'];
    return ENCODERS['image/jpeg'];
}

const renamed = (name, ext) => `${name.replace(/\.[^.]+$/, '')}.${ext}`;

function toFile(blob, file, encoder) {
    return new File([blob], renamed(file.name, encoder.ext), {
        type: encoder.mime,
        lastModified: Date.now(),
    });
}

function canvasToBlob(canvas, mime, quality) {
    return new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) => (blob ? resolve(blob) : reject(new Error('encode-failed'))),
            mime,
            quality ?? undefined,
        );
    });
}

/** The file turned a quarter turn clockwise (or `turns` of them). */
export async function rotateImageFile(file, turns = 1) {
    const rotation = normaliseRotation(turns * 90);
    if (!rotation) return file;

    const image = await loadImageElement(file);
    const source = rotatedSource(image, rotation);
    const encoder = encoderFor(file);

    // A JPEG has no alpha, so anything transparent has to land on white rather
    // than on black, which is what an empty canvas would give it.
    const canvas = document.createElement('canvas');
    canvas.width = source.width;
    canvas.height = source.height;
    const ctx = canvas.getContext('2d', { alpha: encoder.mime !== 'image/jpeg' });
    if (encoder.mime === 'image/jpeg') {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    ctx.drawImage(source, 0, 0);

    return toFile(await canvasToBlob(canvas, encoder.mime, encoder.quality), file, encoder);
}

/**
 * The file cut down to a crop rectangle, given in percentages of the image —
 * the shape the crop dialog hands back.
 */
export async function cropImageFile(file, cropPct) {
    if (!cropPct?.width || !cropPct?.height) return file;

    const image = await loadImageElement(file);
    const width = image.naturalWidth;
    const height = image.naturalHeight;
    const pixelCrop = {
        unit: 'px',
        __natural: true,
        x: Math.max(0, Math.round((cropPct.x / 100) * width)),
        y: Math.max(0, Math.round((cropPct.y / 100) * height)),
        width: Math.max(1, Math.round((cropPct.width / 100) * width)),
        height: Math.max(1, Math.round((cropPct.height / 100) * height)),
    };

    const encoder = encoderFor(file);
    const format = encoder.mime === 'image/png' ? 'png' : encoder.mime === 'image/webp' ? 'webp' : 'jpg';
    const blob = await cropImageToBlob(image, pixelCrop, format);
    return toFile(blob, file, encoder);
}

/**
 * A copy's name.
 *
 * Copies keep their own name so a batch download cannot collapse them into one
 * another: `photo.jpg` → `photo (2).jpg`, skipping any number already taken.
 */
export function copyFileName(name, taken = []) {
    const dot = name.lastIndexOf('.');
    const base = dot > 0 ? name.slice(0, dot) : name;
    const ext = dot > 0 ? name.slice(dot) : '';
    const used = new Set(taken);

    let n = 1;
    let candidate = name;
    while (used.has(candidate)) {
        n += 1;
        candidate = `${base} (${n})${ext}`;
    }
    return candidate;
}

/** A copy of the file under a free name. */
export function duplicateImageFile(file, taken = []) {
    return new File([file], copyFileName(file.name, taken), {
        type: file.type,
        lastModified: Date.now(),
    });
}
